import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronRight, CheckCircle2, Truck, RotateCcw, ShieldAlert, Minus, Plus, Share2, Copy, MessageCircle } from "lucide-react";
import { ProductCard, RecommendedBadge, catName, StoreToTrainingBand } from "../../store/components";
import { useCart, inr } from "../../store/CartContext";
import { toast } from "sonner";

const PROD_DOMAIN = "https://drivewithconfidence.com";

const API = process.env.REACT_APP_BACKEND_URL;

const Block = ({ title, children }) => (
  <div className="rounded-2xl border border-gray-100 bg-white p-6">
    <h2 className="text-sm font-extrabold uppercase tracking-wide text-dwc-charcoal">{title}</h2>
    <div className="mt-3 text-sm leading-relaxed text-dwc-charcoal/70">{children}</div>
  </div>
);

const StoreProduct = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const [qty, setQty] = useState(1);
  const [imgIdx, setImgIdx] = useState(0);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setData(null);
    setQty(1);
    axios.get(`${API}/api/store/products/${slug}`).then((r) => setData(r.data)).catch(() => setMissing(true));
    axios.get(`${API}/api/store/settings`).then((r) => setSettings(r.data)).catch(() => {});
  }, [slug]);

  useEffect(() => {
    const p = data?.product;
    if (!p) return;
    const productUrl = `${PROD_DOMAIN}/store/product/${p.slug}`;
    document.title = `${p.name} — Buy Online | DWC Store, Drive With Confidence`;
    const setMeta = (attr, key, val) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", val);
    };
    const img = (p.images || [])[0] || "";
    const absImg = img.startsWith("http") ? img : `${PROD_DOMAIN}${img}`;
    setMeta("name", "description", `${p.short_benefit || p.description || p.name} Price ${inr(p.price)}. ${p.stock > 0 ? "In stock" : "Out of stock"} at the DWC Store by Drive With Confidence.`.slice(0, 158));
    setMeta("property", "og:type", "product");
    setMeta("property", "og:title", `${p.name} | DWC Store`);
    setMeta("property", "og:description", `${p.short_benefit || p.name} — ${inr(p.price)} from Drive With Confidence.`);
    setMeta("property", "og:image", absImg);
    setMeta("property", "og:url", productUrl);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", `${p.name} | DWC Store`);
    setMeta("name", "twitter:image", absImg);
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) { canon = document.createElement("link"); canon.setAttribute("rel", "canonical"); document.head.appendChild(canon); }
    canon.setAttribute("href", productUrl);
    let ld = document.getElementById("product-jsonld");
    if (!ld) { ld = document.createElement("script"); ld.type = "application/ld+json"; ld.id = "product-jsonld"; document.head.appendChild(ld); }
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Product", name: p.name, image: [absImg], description: p.short_benefit || p.description || "",
          sku: p.id, brand: { "@type": "Brand", name: "Drive With Confidence (DWC)" }, category: catName(p.category), url: productUrl,
          offers: { "@type": "Offer", url: productUrl, priceCurrency: "INR", price: String(p.price), availability: p.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock", itemCondition: "https://schema.org/NewCondition" },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "DWC Store", item: `${PROD_DOMAIN}/store` },
            { "@type": "ListItem", position: 2, name: catName(p.category), item: `${PROD_DOMAIN}/store/shop?cat=${p.category}` },
            { "@type": "ListItem", position: 3, name: p.name, item: productUrl },
          ],
        },
      ],
    });
    return () => { document.getElementById("product-jsonld")?.remove(); };
  }, [data]);

  if (missing)
    return (
      <div className="px-5 pb-24 pt-44 text-center">
        <p className="text-lg font-extrabold text-dwc-charcoal">Product not found.</p>
        <Link to="/store/shop" className="mt-4 inline-block text-sm font-bold uppercase tracking-wide text-dwc-green">Back to Store</Link>
      </div>
    );
  if (!data) return <p className="px-5 pb-24 pt-44 text-center text-sm font-bold uppercase tracking-widest text-dwc-charcoal/40">Loading...</p>;

  const p = data.product;
  const productUrl = `${PROD_DOMAIN}/store/product/${p.slug}`;
  const shareText = `${p.name} — ${inr(p.price)} at the DWC Store (Drive With Confidence): ${productUrl}`;
  const doShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: p.name, text: shareText, url: productUrl }); } catch (e) { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(productUrl);
      toast.success("Product link copied");
    }
  };
  const copyLink = async () => { await navigator.clipboard.writeText(productUrl); toast.success("Product link copied"); };
  const out = (p.stock || 0) < 1;

  return (
    <>
      <section className="bg-white pb-16 pt-32 md:pt-40" data-testid="product-detail-page">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <nav className="flex flex-wrap items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-dwc-charcoal/50" aria-label="Breadcrumb">
            <Link to="/store" className="hover:text-dwc-green">DWC Store</Link>
            <ChevronRight className="h-3 w-3" aria-hidden="true" />
            <Link to={`/store/shop?category=${p.category}`} className="hover:text-dwc-green">{catName(p.category)}</Link>
            <ChevronRight className="h-3 w-3" aria-hidden="true" />
            <span className="text-dwc-charcoal/80">{p.name}</span>
          </nav>

          <div className="mt-8 grid gap-10 lg:grid-cols-2">
            <div>
              <div className="relative overflow-hidden rounded-[2rem] bg-dwc-soft">
                <img src={(p.images || [])[imgIdx] || (p.images || [])[0]} alt={`${p.name} — image ${imgIdx + 1}`} className="aspect-square w-full object-contain" data-testid="product-image" />
                {(p.images || []).length > 1 && (
                  <>
                    <button onClick={() => setImgIdx((i) => (i - 1 + p.images.length) % p.images.length)} aria-label="Previous image" data-testid="gallery-prev" className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-dwc-charcoal shadow-md"><ChevronRight className="h-5 w-5 rotate-180" aria-hidden="true" /></button>
                    <button onClick={() => setImgIdx((i) => (i + 1) % p.images.length)} aria-label="Next image" data-testid="gallery-next" className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-dwc-charcoal shadow-md"><ChevronRight className="h-5 w-5" aria-hidden="true" /></button>
                    <span className="absolute bottom-3 right-3 rounded-full bg-dwc-charcoal/70 px-3 py-1 text-[11px] font-bold text-white" data-testid="gallery-counter">{imgIdx + 1} / {p.images.length}</span>
                  </>
                )}
              </div>
              {(p.images || []).length > 1 && (
                <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid="gallery-thumbs">
                  {p.images.map((im, i) => (
                    <button key={im + i} onClick={() => setImgIdx(i)} aria-label={`View image ${i + 1}`} data-testid={`gallery-thumb-${i}`} className={`h-16 w-16 shrink-0 overflow-hidden rounded-xl border-2 ${i === imgIdx ? "border-dwc-green" : "border-transparent opacity-70"}`}>
                      <img src={im} alt="" className="h-full w-full object-contain bg-dwc-soft" />
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              {p.recommended && <RecommendedBadge />}
              <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-dwc-charcoal md:text-4xl" data-testid="product-name">{p.name}</h1>
              <p className="mt-3 text-base leading-relaxed text-dwc-charcoal/70">{p.short_benefit}</p>
              <div className="mt-5 flex items-baseline gap-3">
                <span className="text-3xl font-black text-dwc-charcoal" data-testid="product-detail-price">{inr(p.price)}</span>
                {p.mrp && p.mrp > p.price && <span className="text-base font-semibold text-dwc-charcoal/40 line-through">{inr(p.mrp)}</span>}
                <span className={`ml-2 text-[11px] font-extrabold uppercase tracking-wider ${out ? "text-dwc-crimson" : "text-dwc-green"}`} data-testid="product-availability">
                  {out ? "Out of Stock" : "In Stock"}
                </span>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-dwc-charcoal/70">{p.description}</p>

              <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="product-share-row">
                <button onClick={doShare} data-testid="share-native-btn" className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-dwc-charcoal hover:border-dwc-green"><Share2 className="h-3.5 w-3.5" aria-hidden="true" /> Share</button>
                <a href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noreferrer" data-testid="share-whatsapp-btn" className="inline-flex items-center gap-1.5 rounded-xl border border-dwc-green/40 px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-dwc-green hover:bg-dwc-soft"><MessageCircle className="h-3.5 w-3.5" aria-hidden="true" /> WhatsApp</a>
                <button onClick={copyLink} data-testid="share-copy-btn" className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-dwc-charcoal hover:border-dwc-green"><Copy className="h-3.5 w-3.5" aria-hidden="true" /> Copy Link</button>
              </div>

              {!out && (
                <div className="mt-7 flex items-center gap-4">
                  <div className="flex items-center rounded-xl border border-gray-200">
                    <button onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Decrease quantity" data-testid="qty-decrease" className="px-3.5 py-3 text-dwc-charcoal/60 hover:text-dwc-green"><Minus className="h-4 w-4" aria-hidden="true" /></button>
                    <span className="min-w-[2.5rem] text-center text-sm font-black text-dwc-charcoal" data-testid="qty-value">{qty}</span>
                    <button onClick={() => setQty((q) => Math.min(20, q + 1))} aria-label="Increase quantity" data-testid="qty-increase" className="px-3.5 py-3 text-dwc-charcoal/60 hover:text-dwc-green"><Plus className="h-4 w-4" aria-hidden="true" /></button>
                  </div>
                </div>
              )}
              <div className="mt-5 flex gap-3">
                <button
                  disabled={out}
                  data-testid="detail-add-to-cart-btn"
                  onClick={() => { add(p, qty); toast.success(`${p.name} added to cart`); }}
                  className="flex min-h-[52px] flex-1 items-center justify-center rounded-xl border-2 border-dwc-green px-4 py-3 text-sm font-bold uppercase leading-[1.2] tracking-[0.02em] text-dwc-green transition-colors hover:bg-dwc-soft disabled:opacity-40"
                >
                  Add to Cart
                </button>
                <button
                  disabled={out}
                  data-testid="detail-buy-now-btn"
                  onClick={() => { add(p, qty); navigate("/store/checkout"); }}
                  className="flex min-h-[52px] flex-1 items-center justify-center rounded-xl bg-dwc-green px-4 py-3 text-sm font-bold uppercase leading-[1.2] tracking-[0.02em] text-white shadow-[0_8px_20px_rgba(23,107,58,0.28)] transition-colors hover:bg-dwc-green-dark disabled:opacity-40"
                >
                  Buy Now
                </button>
              </div>

              <div className="mt-6 space-y-2 text-[13px] font-semibold text-dwc-charcoal/65">
                <p className="flex items-center gap-2"><Truck className="h-4 w-4 text-dwc-green" aria-hidden="true" /> {settings?.delivery_estimate ? `Delivery: ${settings.delivery_estimate} · ` : ""}Free shipping above {inr(settings?.free_shipping_above || 999)}</p>
                <p className="flex items-center gap-2"><RotateCcw className="h-4 w-4 text-dwc-green" aria-hidden="true" /> Returns as per <Link to="/store/policies" className="underline hover:text-dwc-green">Store policies</Link></p>
              </div>
            </div>
          </div>

          <div className="mt-12 grid gap-5 lg:grid-cols-2">
            {p.features?.length > 0 && (
              <Block title="Key Features">
                <ul className="space-y-2">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-dwc-green" aria-hidden="true" />{f}</li>
                  ))}
                </ul>
              </Block>
            )}
            {p.included?.length > 0 && (
              <Block title="What's Included">
                <ul className="space-y-2">
                  {p.included.map((f) => (
                    <li key={f} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-dwc-green" aria-hidden="true" />{f}</li>
                  ))}
                </ul>
              </Block>
            )}
            {p.how_to_use && <Block title="How To Use">{p.how_to_use}</Block>}
            {p.safety_note && (
              <div className="rounded-2xl border border-dwc-crimson/20 bg-dwc-crimson/[0.04] p-6" data-testid="safety-note">
                <h2 className="flex items-center gap-2 text-sm font-extrabold uppercase tracking-wide text-dwc-crimson"><ShieldAlert className="h-4 w-4" aria-hidden="true" /> Important Safety Information</h2>
                <p className="mt-3 text-sm leading-relaxed text-dwc-charcoal/70">{p.safety_note}</p>
              </div>
            )}
          </div>

          {data.related?.length > 0 && (
            <div className="mt-16">
              <h2 className="text-xl font-extrabold tracking-tight text-dwc-charcoal md:text-2xl">You May Also Like</h2>
              <div className="mt-6 grid grid-cols-2 gap-4 md:gap-5 lg:grid-cols-4">
                {data.related.map((r, i) => (
                  <ProductCard key={r.id} product={r} index={i} />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
      <StoreToTrainingBand />
    </>
  );
};

export default StoreProduct;
